import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { I18nProvider } from '@kmgestao/i18n';
import { ThemeProvider, Toaster } from '@kmgestao/ui';

import { App } from './App.js';
import { ErrorBoundary } from './components/ErrorBoundary.js';
import { SessionProvider } from './features/auth/index.js';
import { PaletteThemeProvider } from './features/clinic-appearance/index.js';
import { PwaUpdatePrompt } from './features/pwa/PwaUpdatePrompt.js';
import { initPostHog } from './lib/posthog.js';
import { initSentry } from './lib/sentry.js';

import './styles.css';

// Observabilidade — inicializada antes de qualquer render para capturar erros de boot.
// Noop em dev sem DSN/key; em prod, ambos obrigatórios (configurar no .env.production.local).
initSentry();
initPostHog();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const container = document.getElementById('root');
if (!container) throw new Error('Elemento #root não encontrado em index.html.');

createRoot(container).render(
  <StrictMode>
    <ErrorBoundary>
      <I18nProvider>
        <ThemeProvider defaultTheme="light">
          <QueryClientProvider client={queryClient}>
            <SessionProvider>
              {/* PaletteThemeProvider precisa estar dentro de SessionProvider
                  (lê o JWT pra current_clinic_id) e dentro de QueryClientProvider
                  (usa useQuery pra ler clinic.theme_palette). Quando deslogado
                  ou sem clinic, não injeta paleta e deixa os tokens globais. */}
              <PaletteThemeProvider>
                <BrowserRouter>
                  <App />
                </BrowserRouter>
              </PaletteThemeProvider>
            </SessionProvider>
            <Toaster />
            <PwaUpdatePrompt />
          </QueryClientProvider>
        </ThemeProvider>
      </I18nProvider>
    </ErrorBoundary>
  </StrictMode>,
);
